import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ToolPrepPopup, {
  ToolPrepCloseContext,
} from "../../../shared/Popup/ToolPrepPopup";
import ToolPrepDownloadFlowModals from "../../../shared/Popup/ToolPrepDownloadFlowModals";
import ToolPrepFlatResultStage from "../../../shared/Popup/ToolPrepFlatResultStage";
import ShadowPreview from "./ShadowPreview";
import Shadow from "../Editor/Shadow";
import ImagePickerModal from "../../../../../components/popups/imagePicker/ImagePickerModal";
import { useToolPrepImageSource } from "../../../hooks/useToolPrepImageSource";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import { useToolPrepBakedApplyFlow } from "../../../hooks/useToolPrepBakedApplyFlow";
import { useEditorStore } from "../../../../../state/editorStore";
import { ensureBackgroundRemoved } from "../../../../../utils/ensureBackgroundRemoved";
import { adoptBlobUrl } from "../../../../../utils/adoptBlobUrl";
import { exportShadowPrepImageBlob } from "../../../lib/exportToolPrepEffectToPngBlob";
import { getShadowBleedPx } from "../../../../../utils/dropShadowFilter";
import { hasActiveShadowSettings } from "../../../lib/toolPrepEffectActiveSettings";
import {
  SHADOW_ENTRY_DEFAULT,
  SHADOW_ENTRY_BACKGROUND,
  TOOL_PREP_PREVIEW_BG,
  TOOL_PREP_PREPARING_PREVIEW_LABEL,
  TOOL_PREP_RESULT_FOOTER_HINT,
  initialToolPrepPreviewBg,
} from "../../../../../utils/editorEntrySession";
import {
  TOOL_PREP_CUTOUT_REVEAL_MS,
  useDelayedImageReady,
  useImageNaturalSize,
} from "../../../lib/toolPrepImageLayout";
import ToolPrepPreviewBgCards from "../../../shared/previewBg/ToolPrepPreviewBgCards";
import "./ShadowToolPrep.css";

const SHADOW_THANK_YOU_COPY = {
  titleDownloading: "Downloading…",
  titleComplete: "Download complete",
  leadDownloading: "Please wait — we’re saving your file to this device.",
  leadComplete: "Your file is saved on this computer or phone. You can find it in your Downloads folder.",
  previewAlt: "Shadow image download",
  primaryActionLabel: "Add new image",
};

/**
 * Editing Tools → Shadow prep: auto BG removal, preview-only BG cards, Object Shadow controls.
 */
const ShadowToolPrep = ({
  originalImageUrl = null,
  imageFile = null,
  cachedCutoutUrl = null,
  onCutoutReady = null,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const { getToken } = useAuth();
  const openedFromEditor = useContext(ToolPrepCloseContext) === "editor";
  const setShadow = useEditorStore((s) => s.setShadow);
  const objectShadow = useEditorStore((s) => s.object.shadow);
  const [previewBg, setPreviewBg] = useState(() => initialToolPrepPreviewBg());

  const [cutoutUrl, setCutoutUrl] = useState(cachedCutoutUrl || null);
  const [creationId, setCreationId] = useState(null);
  const [bgLoading, setBgLoading] = useState(false);
  const [bgError, setBgError] = useState(null);
  const [doneBusy, setDoneBusy] = useState(false);
  const seededShadowRef = useRef(false);
  const subjectPlacementRef = useRef(null);
  const bgStartedRef = useRef(false);
  const scrollBodyRef = useRef(null);
  const resetAppliedRef = useRef(() => {});

  const {
    sourceImageUrl,
    resolvedFile,
    sourceFileName,
    sourceFileSizeLabel,
    clearSource,
    openUploadPicker,
    imagePickerProps,
    uploadProgress,
  } = useToolPrepImageSource({
    originalImageUrl,
    imageFile,
    busy: bgLoading || doneBusy,
    onSourceChange: () => {
      setCutoutUrl(null);
      setCreationId(null);
      setBgError(null);
      bgStartedRef.current = false;
      resetAppliedRef.current();
      subjectPlacementRef.current = null;
      setPreviewBg(initialToolPrepPreviewBg());
    },
  });

  const {
    applied: shadowApplied,
    busy: shadowBusy,
    resultUrl: shadowPreviewUrl,
    applyError: shadowApplyError,
    setApplyError: setShadowApplyError,
    apply: applyShadow,
    resetApplied,
    downloadBusy,
    handleDownload,
    downloadFlow,
  } = useToolPrepBakedApplyFlow({
    sourceFileName,
    fallbackDownloadName: "shadow.png",
    toolKey: "shadow",
    shareTitle: "Shadow tool",
    shareText: "I added a shadow to my photo with LazyStudio's Shadow tool!",
    thankYouCopy: SHADOW_THANK_YOU_COPY,
  });

  resetAppliedRef.current = resetApplied;

  useEffect(() => {
    if (cachedCutoutUrl) setCutoutUrl(cachedCutoutUrl);
  }, [cachedCutoutUrl]);

  useEffect(() => {
    if (seededShadowRef.current) return;
    seededShadowRef.current = true;
    setShadow({ ...SHADOW_ENTRY_DEFAULT }, { recordHistory: false });
  }, [setShadow]);

  const runBackgroundRemoval = useCallback(async () => {
    if (cutoutUrl?.startsWith("blob:") || cutoutUrl?.startsWith("data:")) {
      return { cutoutUrl, creationId };
    }
    if (
      cachedCutoutUrl?.startsWith("blob:") ||
      cachedCutoutUrl?.startsWith("data:")
    ) {
      setCutoutUrl(cachedCutoutUrl);
      return { cutoutUrl: cachedCutoutUrl, creationId };
    }

    setBgLoading(true);
    setBgError(null);
    try {
      const token = await getToken();
      const { cutoutUrl: nextUrl, creationId: cid } =
        await ensureBackgroundRemoved({
          token,
          imageFile: resolvedFile,
          imageUrl: sourceImageUrl,
          cachedCutoutUrl: cutoutUrl || cachedCutoutUrl,
        });
      setCutoutUrl(nextUrl);
      if (cid != null) setCreationId(cid);
      onCutoutReady?.(nextUrl);
      return { cutoutUrl: nextUrl, creationId: cid ?? creationId };
    } catch (err) {
      setBgError(err?.message || "Failed to remove background");
      throw err;
    } finally {
      setBgLoading(false);
    }
  }, [
    cachedCutoutUrl,
    creationId,
    cutoutUrl,
    getToken,
    onCutoutReady,
    resolvedFile,
    sourceImageUrl,
  ]);

  useEffect(() => {
    if (bgStartedRef.current) return;
    if (!resolvedFile && !sourceImageUrl) return;
    bgStartedRef.current = true;
    runBackgroundRemoval().catch(() => {});
  }, [resolvedFile, runBackgroundRemoval, sourceImageUrl]);

  const previewImageUrl = cutoutUrl || null;
  const cutoutRevealReady = useDelayedImageReady(
    previewImageUrl,
    TOOL_PREP_CUTOUT_REVEAL_MS
  );
  const revealCutoutUrl = cutoutRevealReady ? previewImageUrl : null;
  const cutoutNaturalSize = useImageNaturalSize(revealCutoutUrl);
  const shadowBleedPx = getShadowBleedPx(objectShadow);

  const handlePreviewBgChange = (nextBg) => {
    resetApplied();
    setShadowApplyError(null);
    setPreviewBg(nextBg);
  };

  const resolveBackgroundColor = () =>
    previewBg === TOOL_PREP_PREVIEW_BG.WHITE
      ? SHADOW_ENTRY_BACKGROUND
      : previewBg === TOOL_PREP_PREVIEW_BG.COLOR
        ? SHADOW_ENTRY_BACKGROUND
        : null;

  const buildExport = useCallback(() => {
    const url = revealCutoutUrl;
    const shadowState = { ...objectShadow };
    const backgroundColor = resolveBackgroundColor();
    return () =>
      exportShadowPrepImageBlob(url, {
        shadowState,
        outlineState: { enabled: false },
        previewBg,
        backgroundColor,
        uploadedImageUrl: sourceImageUrl,
      });
  }, [
    objectShadow,
    previewBg,
    revealCutoutUrl,
    sourceImageUrl,
  ]);

  const handleApplyShadow = async () => {
    if (!revealCutoutUrl || shadowBusy || !hasActiveShadowSettings(objectShadow)) {
      return;
    }
    await applyShadow(buildExport());
  };

  /** Keep a durable original so Remove BG toggle OFF can restore the upload. */
  const resolveOriginalForEditor = async () => {
    if (!sourceImageUrl) return null;
    try {
      return await adoptBlobUrl(sourceImageUrl);
    } catch {
      return sourceImageUrl;
    }
  };

  // Editor already has this cutout. Apply only keeps the shadow settings.
  const handleApplyToEditor = async () => {
    if (!revealCutoutUrl || !hasActiveShadowSettings(objectShadow)) return;
    onDone({
      imageUrl: revealCutoutUrl,
      originalImageUrl: await resolveOriginalForEditor(),
      creationId,
      shadow: { ...objectShadow },
      previewBg,
      backgroundColor: resolveBackgroundColor(),
      entryFlow: "shadow",
    });
  };

  const handleAdjustShadow = () => {
    setShadowApplyError(null);
    resetApplied();
  };

  const handleDone = async () => {
    setDoneBusy(true);
    try {
      const originalForEditor = await resolveOriginalForEditor();
      // Open in Editor: live cutout + CSS shadow + stage bg (not baked PNG).
      // Do not set preserveShadowLayout — that forces full-frame BB (AI soft
      // overflow). Object shadow needs opaque-subject BB like the tool canvas.
      if (shadowApplied && (cutoutUrl || revealCutoutUrl)) {
        onDone({
          imageUrl: cutoutUrl || revealCutoutUrl,
          originalImageUrl: originalForEditor,
          creationId,
          shadow: { ...objectShadow },
          previewBg,
          backgroundColor: resolveBackgroundColor(),
          entryFlow: "shadow",
        });
        return;
      }

      let imageUrl = cutoutUrl;
      let doneCreationId = creationId;
      if (!imageUrl) {
        const removed = await runBackgroundRemoval();
        imageUrl = removed?.cutoutUrl;
        doneCreationId = removed?.creationId ?? doneCreationId;
      }
      if (!imageUrl) {
        throw new Error("Background-removed image is not ready");
      }
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:")
      ) {
        throw new Error(
          "Background-removed image could not be loaded for the editor (CORS). Please try again."
        );
      }
      onDone({
        imageUrl,
        originalImageUrl: originalForEditor,
        creationId: doneCreationId,
        shadow: { ...objectShadow },
        previewBg,
        backgroundColor: resolveBackgroundColor(),
        entryFlow: "shadow",
      });
    } catch (err) {
      setBgError(err?.message || "Could not finish shadow setup");
    } finally {
      setDoneBusy(false);
    }
  };

  const canApplyShadow =
    Boolean(sourceImageUrl) &&
    Boolean(revealCutoutUrl) &&
    hasActiveShadowSettings(objectShadow) &&
    !shadowBusy;

  const configureFooterHint = !sourceImageUrl
    ? "Upload an image to start adding a shadow."
    : !revealCutoutUrl
      ? "Removing background…"
      : !hasActiveShadowSettings(objectShadow)
        ? "Enable shadow in the controls, then click Apply Shadow."
        : "Adjust the shadow, then click Apply Shadow.";

  const leftContent = (
    <div className="stp-panel">
      <div className="stp-preview-bg-block">
        <ToolPrepPreviewBgCards
          previewBg={previewBg}
          onPreviewBgChange={handlePreviewBgChange}
          cutoutUrl={revealCutoutUrl}
          uploadedImageUrl={sourceImageUrl}
          cutoutPending={Boolean(sourceImageUrl) && !cutoutRevealReady}
          showDropShadowOnCutout
          onUploadClick={openUploadPicker}
          onRemove={clearSource}
          uploadProgress={uploadProgress}
          sourceName={sourceFileName}
          sourceSizeLabel={sourceFileSizeLabel}
          actionsDisabled={bgLoading || doneBusy || downloadBusy}
          removeBgControls={{
            active: true,
            locked: true,
            bio: "Required — background is removed so the shadow sits on the subject cutout.",
            error: bgError,
          }}
        />
      </div>

      <div
        className={`stp-shadow-controls tool-prep-scroll${
          shadowApplied ? " stp-shadow-controls--locked" : ""
        }`}
        ref={scrollBodyRef}
      >
        <Shadow defaultExpanded forceExpanded />
      </div>
    </div>
  );

  useSyncScrollbarWidth(scrollBodyRef, [sourceImageUrl, cutoutUrl, shadowApplied]);

  return (
    <>
      <ImagePickerModal {...imagePickerProps} />
      <ToolPrepPopup
        toolKey="shadow"
        title="Shadow"
        left={leftContent}
        preview={
          shadowApplied ? (
            <ToolPrepFlatResultStage
              className="shadow-tool-preview"
              resultImageUrl={shadowPreviewUrl}
              previewBg={previewBg}
              frameNaturalSize={cutoutNaturalSize}
              subjectNaturalSize={cutoutNaturalSize}
              shadowBleedPx={shadowBleedPx}
              subjectPlacement={subjectPlacementRef.current}
              applying={shadowBusy}
              applyingLabel="Applying shadow…"
              resultAlt="Shadow result"
              softWatermarkToolKey="shadow"
              emptyLabel={
                uploadProgress != null && !sourceImageUrl
                  ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                  : undefined
              }
            />
          ) : (
            <ShadowPreview
              imageUrl={revealCutoutUrl}
              uploadedImageUrl={sourceImageUrl}
              loading={Boolean(sourceImageUrl) && !cutoutRevealReady}
              applying={shadowBusy}
              applyingLabel="Applying shadow…"
              previewBg={previewBg}
              showSubjectBox={Boolean(revealCutoutUrl)}
              onSubjectPlacement={(placement) => {
                subjectPlacementRef.current = placement;
              }}
              emptyLabel={
                uploadProgress != null && !sourceImageUrl
                  ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                  : undefined
              }
            />
          )
        }
        onDone={
          openedFromEditor
            ? handleApplyToEditor
            : shadowApplied
              ? handleDone
              : handleApplyShadow
        }
        onCancel={onCancel}
        confirmClose={Boolean(sourceImageUrl)}
        onDownload={
          openedFromEditor
            ? null
            : shadowApplied
              ? () => handleDownload(buildExport())
              : null
        }
        onOpenInEditor={
          openedFromEditor ? null : shadowApplied ? handleDone : null
        }
        onSecondaryAction={
          openedFromEditor ? null : shadowApplied ? handleAdjustShadow : null
        }
        secondaryActionLabel="Adjust shadow"
        busy={
          openedFromEditor
            ? Boolean(bgLoading && !cutoutUrl)
            : shadowBusy || doneBusy || (bgLoading && !cutoutUrl) || downloadBusy
        }
        busyLabel="Applying shadow…"
        showDone={openedFromEditor || !shadowApplied}
        doneDisabled={!canApplyShadow}
        doneLabel="Apply Shadow"
        footerActionOrder={
          shadowApplied
            ? ["cancel", "secondary", "download", "openInEditor"]
            : ["cancel", "primary"]
        }
        openInEditorLabel="Open in Editor"
        footerHint={
          shadowApplyError ||
          (shadowApplied ? TOOL_PREP_RESULT_FOOTER_HINT : configureFooterHint)
        }
        footerHintClassName={
          shadowApplyError ? "tool-prep-popup__footer-hint--error" : ""
        }
      />
      <ToolPrepDownloadFlowModals
        flow={downloadFlow}
        toolKey="shadow"
        onPrimaryAction={() => {
          downloadFlow.handleThankYouDismiss();
          clearSource();
        }}
        onBackToDashboard={onCancel}
      />
    </>
  );
};

export default ShadowToolPrep;
